import { PrivateRoute, PublicPageRoute, PublicRoute, Router } from '@core/router';
import HomePage from '@modules/public/HomePage';
import React, { lazy, memo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { UserRole } from '@business/entities/account';
import { Config } from '@config';
import { ShortAppRouter } from './ShortAppRouter';
import { RoleService } from '@business/services';

const DepositInvoicePaymentPage = lazy(() => import('@modules/proposals/DepositPaymentPage/Invoice'));
const LoginPage = lazy(() => import('@modules/auth/LoginPage'));
const ResetPasswordPage = lazy(() => import('@modules/auth/ResetPasswordPage'));
const CreatePasswordPage = lazy(() => import('@modules/auth/CreatePasswordPage'));
const RingCentralRedirect = lazy(() => import('@modules/auth/RingCentralRedirect'));
const ActivateAccountPage = lazy(() => import('@modules/auth/ActivateAccountPage'));
const MainPage = lazy(() => import('@modules/dashboard/DashboardPage'));
const PublicPageLayout = lazy(() => import('@modules/public/PublicPageLayout'));
const MediaPageLayout = lazy(() => import('@modules/public/MediaPageLayout'));
const NotFroundPage = lazy(() => import('@modules/pages/NotFroundPage'));
const UnauthorizedPage = lazy(() => import('@modules/pages/UnauthorizedPage'));
const CompanyPage = lazy(() => import('@modules/public/CompanyPage'));
const CategoriesPage = lazy(() => import('@modules/public/CategoriesPage'));
const PaymentPage = lazy(() => import('@modules/PaymentPage'));
const CheckoutPage = lazy(() => import('@modules/CheckoutPage'));
const CheckoutPayPage = lazy(() => import('@modules/CheckoutPage/CheckoutPayPage'));
const JobDescriptionPrint = lazy(() => import('@modules/JobDescriptionPrint'));
const PurchasePrint = lazy(() => import('@modules/PurchasePrint'));
const PurchaseLadingPrint = lazy(() => import('@modules/PurchaseLadingPrint/PurchaseLadingPrint'));
const PurchasePickListPrint = lazy(() => import('@modules/PurchaseLadingPrint/PurchasePickListPrint'));
const ShipmentPrint = lazy(() => import('@modules/ShipmentPrint'));
const ArticlePage = lazy(() => import('@modules/public/ArticlePage'));
const EstimatePage = lazy(() => import('@modules/public/EstimatePage'));
const EstimatePrint = lazy(() => import('@modules/public/EstimatePrint'));
const InvoicePrint = lazy(() => import('@modules/public/InvoicePrint'));
const DashboardPrint = lazy(() => import('@modules/public/DashboardPrint'));
const SingnUpPage = lazy(() => import('@modules/auth/SingnUpPage'));
const ControlAccount = lazy(() => import('@modules/auth/ControlAccount'));
const FormPage = lazy(() => import('@modules/public/FormPage'));
const PublicFormPage = lazy(() => import('@modules/public/PublicFormPage'));
const OAuthConnectionPage = lazy(() => import('@modules/auth/OAuthConnection'));
const connectRavePage = lazy(() => import('@modules/connection/connectRave'));
const ViewLibraryItemPage = lazy(() => import('@modules/public/ViewLibraryItemPage'));
const AuthorizePage = lazy(() => import('@modules/auth/AuthorizePage'));
const AddPaymentMethodPage = lazy(() => import('@modules/AddPaymentMethodPage'));
const PublicLibraryItemPage = lazy(() => import('@modules/public/PublicLibraryItemPage'));
const ShareProjectBoardPage = lazy(() => import('@modules/board/BoardPage/ShareProjectBoardPage'));
const ProposalPreviewPage = lazy(() => import('@modules/proposal/ProposalPreviewPage'));

const TemplatePreviewPage = lazy(() => import('@modules/settings/SettingsPage/Components/TemplateTab/template/TemplatePreviewPage'));
const TemplatesPreviewPage = lazy(() => import('@modules/settings/SettingsPage/Components/TemplateTab/templates/ViewTemplateModal/TemplatePreviewPage'));
const ProposalsPreviewPage = lazy(() => import('@modules/proposals/ViewProposalModal/ProposalPreviewPage'));
const ProposalDocumentPage = lazy(() => import('@modules/proposals/ProposalDocumentPage'));
const CourseBuilderPreviewPage = lazy(() => import('@modules/course/CourseBuilder/CourseBuilderPreviewPage'));
const PublicDocumentEditor = lazy(() => import('@modules/common/PublicDocumentEditor'));
const DepositPaymentPage = lazy(() => import('@modules/proposals/DepositPaymentPage'));
const knowledgeBasePage = lazy(() => import('@modules/knowledgeBase/knowledgeBasePage'));
const KnowledgeBaseView = lazy(() => import('@modules/knowledgeBase/KnowledgeBaseView'));
const KbSearchPage = lazy(() => import('@modules/knowledgeBase/KbSearchPage'));
const ControlAccountByEmail = lazy(() => import('@modules/auth/ControlAccountByEmail'));
const PaymentPageVendasta = lazy(() => import('@modules/PaymentPageVendasta'));
const SchedulePage = lazy(() => import('@modules/CalendarPage/SchedulePage'));
const TmTimeTrackingPage = lazy(() => import('@modules/public/TmTimeTrackingPage'));
const TmProjectProgressReportPage = lazy(() => import('@modules/public/TmProjectProgressReportPage'));
const VccItemsPrintPage = lazy(() => import('@modules/public/VccItemsPrint'));
const TMChangeOrderPreviewPage = lazy(() => import('@modules/timeMaterials/TMChangeOrderPreviewPage'));
const TMChangeOrderDepositPage = lazy(() => import('@modules/timeMaterials/TMChangeOrderPreviewPage/DepositPaymentPage'));
const CoursesPublicPage = lazy(() => import('@modules/course/CoursePublicPage'));
const ViewLibraryItemPublic = lazy(() => import('@modules/LibraryPage/ViewLibraryItemPublic'));
const OpenEmailLinkPage = lazy(() => import('@modules/OpenEmailLinkPage'));
const PresentationPage = lazy(() => import('@modules/public/PresentationPage'));

export const AppRouter = memo(() => {
  const hostname = document.location.hostname;
  if (Config?.GC_SHORT_URL_CAL?.includes(hostname) || Config?.GC_SHORT_URL?.includes(hostname)) {
    return <ShortAppRouter />;
  }
  return (
    <div>
      <Router>
        <Switch>
          <PrivateRoute exact path="/oauth-connect" component={connectRavePage} />
          <PublicRoute exact path="/proposal-preview/:appId/:type/:id" component={ProposalPreviewPage} tags={['Proposal']} />
          <PrivateRoute
            exact
            path="/courses/lesson/preview/:id"
            component={CourseBuilderPreviewPage}
            roles={RoleService.staffRoles()}
            tags={['Training']}
          />
          <PublicRoute exact path="/public-course/pub/view/:tid/:id/:userId" component={CoursesPublicPage} />
          <PublicRoute exact path={['/public-library/:id/:mode/:type/:itemId']} component={ViewLibraryItemPublic} />

          {/* Public Page / Agreement preview. `/template-preview/…` is the canonical link; the old
              `/setting/template-preview/…` path stays so links already shared keep working. */}
          <PublicRoute exact path={['/template-preview/:appId/:type/:id', '/setting/template-preview/:appId/:type/:id']} component={TemplatePreviewPage} tags={['Proposal']} />
          <PublicPageRoute exact path="/setting/template-preview" component={TemplatesPreviewPage} tags={['Proposal']} />

          <PublicPageRoute exact path="/proposal-preview" component={ProposalsPreviewPage} tags={['Proposal']} />
          {/* A Terms & Conditions document link — renders the copy frozen into the proposal when it was sent */}
          <PublicPageRoute exact path="/proposal-document" component={ProposalDocumentPage} tags={['Proposal']} />
          <PublicPageRoute exact path="/proposal/deposit" component={DepositPaymentPage} tags={['Proposal']} />
          <PublicPageRoute exact path="/invoice/deposit" component={DepositInvoicePaymentPage} tags={['Proposal']} />
          <PublicPageRoute exact path="/change-order-preview" component={TMChangeOrderPreviewPage} />
          <PublicPageRoute exact path="/change-order-deposit" component={TMChangeOrderDepositPage} />

          <PublicRoute exact path="/public" component={PublicPageLayout} />
          <PublicRoute path="/form/:fileId" component={FormPage} />

          <PublicRoute exact path="/document-editor" component={PublicDocumentEditor} />

          {/* start public routes moved from public router */}
          <PublicPageRoute exact path="/public/:company/search" component={HomePage} />
          <PublicPageRoute exact path="/public/:company" component={CompanyPage} />
          <PublicPageRoute exact path="/public/:company/:category" component={CategoriesPage} />
          <PublicPageRoute exact path="/public/:company/:category/:article" component={ArticlePage} />
          <PublicPageRoute exact path="/public/library/view/:appId/:type/:id" component={PublicLibraryItemPage} />
          <PublicPageRoute exact path="/board/shared-project/:id" component={ShareProjectBoardPage} />
          <PublicRoute exact path="/forms/:slug" component={PublicFormPage} />
          <PublicRoute exact path="/signup-seo/:id" component={PaymentPageVendasta} />

          <PublicPageRoute exact path="/time-material/time-tracked/:id" component={TmTimeTrackingPage} />

          <PublicPageRoute exact path="/time-material/project-progress/:id" component={TmProjectProgressReportPage} />

          <PublicPageRoute exact path="/time-material/vcc-items-print" component={VccItemsPrintPage} />

          {/* end public routes moved from public router */}

          <PublicRoute exact path="/payment-method/add" component={AddPaymentMethodPage} />
          <PublicRoute exact path="/invoice/pay" component={PaymentPage} />
          <PublicRoute exact path="/estimate/view" component={EstimatePage} />

          <PublicRoute exact path="/checkout" component={CheckoutPage} />
          <PublicRoute exact path="/checkout/pay" component={CheckoutPayPage} />
          <PublicRoute exact path="/job-description/print" component={JobDescriptionPrint} />
          <PublicRoute exact path="/estimate/print" component={EstimatePrint} />
          <PublicRoute exact path="/invoice/print" component={InvoicePrint} />
          <PublicRoute exact path="/dashboard/print" component={DashboardPrint} />
          <PublicRoute exact path="/po/print" component={PurchasePrint} />
          <PublicRoute exact path="/po/lading-print" component={PurchaseLadingPrint} />
          <PublicRoute exact path="/po/pick-list-print" component={PurchasePickListPrint} />
          <PublicRoute exact path="/shipment/print" component={ShipmentPrint} />

          <PublicRoute exact path="/share/:appId/:companyId/:hash/:fileName" component={MediaPageLayout} />
          <PublicRoute exact path="/login" component={LoginPage} />
          <PublicRoute exact path="/oauth-connection" component={OAuthConnectionPage} />
          <PublicRoute exact path="/signUp" component={SingnUpPage} />
          <PublicRoute exact path="/ringcentral-redirect" component={RingCentralRedirect} />
          <PublicRoute exact path="/reset-password" component={ResetPasswordPage} />
          <PublicRoute exact path="/create-password" component={CreatePasswordPage} />
          <PublicRoute exact path="/activate/:appId/:token" component={ActivateAccountPage} />
          <PublicRoute exact path="/schedule/:uname/:slug" component={SchedulePage} />
          <PublicRoute exact path="/404" component={NotFroundPage} />
          <PublicRoute exact path={['/401', '/401-external']} component={UnauthorizedPage} />

          <PublicRoute exact path="/control-account/:appId" component={ControlAccount} />
          <PublicRoute exact path="/control-account-by-email/:appId/:email" component={ControlAccountByEmail} />

          <PublicRoute exact path="/view/:appId/:type/:id" component={ViewLibraryItemPage} />

          <PrivateRoute exact path="/oauth/authorize" component={AuthorizePage} roles={[UserRole.admin]} />

          {/* Kowledge Base */}
          <PublicRoute exact path="/kb/search" component={KbSearchPage} />
          <PublicRoute exact path="/kb/view/:id/:slug?" component={KnowledgeBaseView} />
          <PublicRoute exact path="/kb/:id?" component={knowledgeBasePage} />

          {/* Presentation */}

          <PublicRoute exact path={['/presentation/:id/:orgId/preview']} component={PresentationPage} />
          <PublicRoute exact path={['/presentation-template/:id/preview/:appId/:companyId']} component={PresentationPage} />
          <PublicRoute exact path={['/presentation/:id', '/presentation-template/:id/preview/:appId/:companyId', '/presentation-template/:id/preview/:appId/:companyId/:isPublic']} component={PresentationPage} />

          <PublicRoute exact path="/open-email" component={OpenEmailLinkPage} />

          <PrivateRoute path="/" component={MainPage} />

          <Route component={NotFroundPage} />
        </Switch>
      </Router>
    </div>
  );
});
